import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  KeyboardEvent,
} from 'react';
import { SendHorizontal, Square, X, Database, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { CollectionMention } from './collection-mention';
import {
  filterCollectionReferences,
  referenceKey,
  type CollectionReference,
} from '@/utils/collection-references';

const textareaClassName =
  'min-h-9 max-h-[150px] resize-none overflow-y-auto border-0 bg-transparent dark:bg-transparent px-3 py-1.5 text-sm leading-normal shadow-none outline-none ring-0 focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border';

/** Trailing `@word` the caret sits inside; group 1 is the preceding whitespace. */
const MENTION_PATTERN = /(^|\s)@([^\s@]*)$/;

const MAX_MATCHES = 50;

export interface ChatAgentInputProps {
  onSubmit: (message: string) => void;
  onStop?: () => void;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  autoFocus?: boolean;
  focusTrigger?: boolean;
  className?: string;
  /** Data Collections the user can reference with `@`. */
  collectionReferences?: CollectionReference[];
  isLoadingCollections?: boolean;
  /** References staged for the next message, owned by the page that sends it. */
  attachedReferences?: CollectionReference[];
  onAttachReference?: (reference: CollectionReference) => void;
  onRemoveReference?: (key: string) => void;
}

export const ChatAgentInput: React.FC<ChatAgentInputProps> = ({
  onSubmit,
  onStop,
  placeholder = 'Type something ...',
  disabled = false,
  isLoading = false,
  autoFocus = false,
  focusTrigger = false,
  className,
  collectionReferences = [],
  isLoadingCollections = false,
  attachedReferences = [],
  onAttachReference,
  onRemoveReference,
}) => {
  const [value, setValue] = useState('');
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const mentionEnabled =
    !!onAttachReference &&
    (isLoadingCollections || collectionReferences.length > 0);
  const isMentionOpen = mentionEnabled && mentionQuery !== null;

  const selectedKeys = useMemo(
    () => new Set(attachedReferences.map(referenceKey)),
    [attachedReferences],
  );

  const matches = useMemo(
    () =>
      mentionQuery === null
        ? []
        : filterCollectionReferences(collectionReferences, mentionQuery).slice(
            0,
            MAX_MATCHES,
          ),
    [collectionReferences, mentionQuery],
  );

  const syncMentionQuery = (next: string) => {
    if (!mentionEnabled) return;
    const match = next.match(MENTION_PATTERN);
    setMentionQuery(match ? match[2] : null);
    setActiveIndex(0);
  };

  const handleChange = (next: string) => {
    setValue(next);
    syncMentionQuery(next);
  };

  const attachReference = (reference: CollectionReference) => {
    // An already-attached pick skips the callback but still clears the
    // fragment and closes the list, so the key press isn't a dead end.
    if (!selectedKeys.has(referenceKey(reference))) {
      onAttachReference?.(reference);
    }
    // Drop the `@query` fragment, keeping the whitespace that preceded it.
    setValue((prev) => prev.replace(MENTION_PATTERN, '$1'));
    setMentionQuery(null);
    setActiveIndex(0);
    textareaRef.current?.focus();
  };

  const handleSubmit = () => {
    const trimmedValue = value.trim();
    if (trimmedValue && !disabled && !isLoading) {
      onSubmit(trimmedValue);
      setValue('');
      setMentionQuery(null);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // The caret never leaves the textarea, so the list cannot receive keys
    // itself and navigation has to be handled here.
    if (isMentionOpen) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMentionQuery(null);
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (matches.length === 0) return;
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setActiveIndex(
          (prev) => (prev + step + matches.length) % matches.length,
        );
        return;
      }
      const highlighted = matches[activeIndex];
      if (
        (event.key === 'Enter' || event.key === 'Tab') &&
        !event.shiftKey &&
        highlighted
      ) {
        event.preventDefault();
        attachReference(highlighted);
        return;
      }
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const next = Math.min(textarea.scrollHeight, 150);
    textarea.style.height = `${next}px`;
  }, [value]);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  const isDisabled = disabled || isLoading;

  useEffect(() => {
    if (focusTrigger && !isDisabled && textareaRef.current) {
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    }
  }, [focusTrigger, isDisabled]);

  const canSubmit = value.trim().length > 0 && !isDisabled;

  const handleButtonClick = () => {
    if (isLoading && onStop) {
      onStop();
    } else {
      handleSubmit();
    }
  };

  return (
    <div className='relative'>
      {isMentionOpen && (
        <CollectionMention
          matches={matches}
          activeIndex={activeIndex}
          selectedKeys={selectedKeys}
          onSelect={attachReference}
          onHover={setActiveIndex}
          isLoading={isLoadingCollections}
        />
      )}
      <div
        className={cn(
          'flex flex-col gap-1 rounded-3xl border border-border bg-sidebar py-1 pl-1 pr-1',
          className,
        )}
      >
        {attachedReferences.length > 0 && (
          <div className='flex flex-wrap gap-1 px-2 pt-1'>
            {attachedReferences.map((reference) => {
              const key = referenceKey(reference);
              const Icon = reference.kind === 'dataset' ? Database : FileText;
              return (
                <span
                  key={key}
                  className='flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground'
                >
                  <Icon className='size-3 shrink-0' />
                  <span className='max-w-40 truncate'>{reference.name}</span>
                  <button
                    type='button'
                    onClick={() => onRemoveReference?.(key)}
                    aria-label={`Remove ${reference.name}`}
                    className='cursor-pointer rounded-full hover:text-foreground'
                  >
                    <X className='size-3' />
                  </button>
                </span>
              );
            })}
          </div>
        )}
        <div className='flex items-end gap-1'>
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => setMentionQuery(null)}
            placeholder={placeholder}
            aria-label={placeholder || 'Chat message input'}
            disabled={isDisabled}
            rows={1}
            className={cn(textareaClassName, 'w-full min-w-0 flex-1')}
          />
          {isLoading && onStop ? (
            <button
              type='button'
              onClick={handleButtonClick}
              aria-label='Stop generating'
              className='relative flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-accent hover:text-foreground cursor-pointer'
            >
              <svg
                className='absolute inset-0 size-9 animate-spin p-0.5'
                viewBox='0 0 24 24'
                fill='none'
              >
                <circle
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='2'
                  className='text-zinc-200 dark:text-zinc-700'
                />
                <path
                  d='M12 2a10 10 0 0 1 10 10'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  className='text-primary'
                />
              </svg>
              <Square className='relative size-3 fill-current' />
            </button>
          ) : (
            <button
              type='button'
              onClick={handleButtonClick}
              aria-label='Send message'
              disabled={!canSubmit}
              className={cn(
                'flex size-9 shrink-0 items-center justify-center rounded-full transition-colors',
                canSubmit
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer'
                  : 'bg-muted text-muted-foreground/40 cursor-not-allowed',
              )}
            >
              <SendHorizontal className='size-4' />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatAgentInput;
